import { useCallback, useEffect } from 'react';
import { useMarzipanoController } from '../context/MarzipanoControllerContext';
import { tourConfig } from '../data/tour.config';
import { sceneLabel, translate } from '../i18n';
import {
  isFullscreen as checkFullscreen,
  isFullscreenSupported,
  toggleFullscreen as toggleFullscreenMode,
} from '../lib/fullscreen';
import { useTourStore } from '../hooks/useTourStore';
import logoMark from '../assets/figma/logo-mark.svg';
import logoText from '../assets/figma/logo-text.svg';
import logoCity from '../assets/figma/logo-city.svg';
import sidebarToggleIcon from '../assets/figma/icon-sidebar-toggle.svg';
import autorotateStartIcon from '../assets/figma/icon-autorotate-start.svg';
import autorotateStopIcon from '../assets/figma/icon-rotate-360.svg';
import fullscreenIcon from '../assets/figma/icon-fullscreen.svg';
import styles from './Toolbar.module.css';

export function Toolbar() {
  const locale = useTourStore((s) => s.locale);
  const currentSceneId = useTourStore((s) => s.currentSceneId);
  const isSceneListOpen = useTourStore((s) => s.isSceneListOpen);
  const isAutorotating = useTourStore((s) => s.isAutorotating);
  const isFullscreen = useTourStore((s) => s.isFullscreen);
  const toggleSceneList = useTourStore((s) => s.toggleSceneList);
  const setFullscreen = useTourStore((s) => s.setFullscreen);
  const controller = useMarzipanoController();

  useEffect(() => {
    const onChange = () => setFullscreen(checkFullscreen());
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, [setFullscreen]);

  const handleToggleFullscreen = useCallback(async () => {
    if (!isFullscreenSupported()) return;
    await toggleFullscreenMode();
    setFullscreen(checkFullscreen());
  }, [setFullscreen]);

  const autorotateLabel = translate(
    locale,
    isAutorotating ? 'app.autorotateStop' : 'app.autorotateStart'
  );
  const fullscreenLabel = translate(locale, isFullscreen ? 'app.exitFullscreen' : 'app.fullscreen');

  return (
    <header className={styles.toolbar}>
      <a
        className={styles.brand}
        href={tourConfig.links.mainWebsite}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="International Hi-Tech Healthcare Park"
      >
        <img className={styles.logoMark} src={logoMark} width="45.9609" height="46" alt="" />
        <span className={styles.brandText}>
          <img src={logoText} width="161.501" height="22.9" alt="" />
          <img src={logoCity} width="64.914" height="7.911" alt="" />
        </span>
      </a>

      <button
        type="button"
        className={styles.sidebarToggle}
        onClick={toggleSceneList}
        aria-pressed={isSceneListOpen}
        aria-label={translate(locale, 'app.sceneList')}
        title={translate(locale, 'app.sceneList')}
      >
        <img
          className={isSceneListOpen ? styles.sidebarToggleOpen : undefined}
          src={sidebarToggleIcon}
          width="24"
          height="24"
          alt=""
        />
      </button>

      <h1 className={styles.title}>{currentSceneId ? sceneLabel(locale, currentSceneId) : ''}</h1>

      <div className={styles.actions}>
        <button
          type="button"
          className={`${styles.action} ${styles.actionLight} ${isAutorotating ? styles.actionActive : ''}`}
          onClick={() => controller?.toggleAutorotate()}
          aria-pressed={isAutorotating}
          aria-label={autorotateLabel}
          title={autorotateLabel}
        >
          <img
            src={isAutorotating ? autorotateStopIcon : autorotateStartIcon}
            width="32"
            height="32"
            alt=""
          />
        </button>
        <button
          type="button"
          className={`${styles.action} ${styles.actionDark}`}
          onClick={handleToggleFullscreen}
          aria-pressed={isFullscreen}
          aria-label={fullscreenLabel}
          title={fullscreenLabel}
        >
          <img src={fullscreenIcon} width="32" height="32" alt="" />
        </button>
      </div>
    </header>
  );
}
